/* Buffy clip registry for the teacher hero (src/Homepage/connect/buffy/).

   HOW TO REGISTER A NEW CLIP (this is the whole job):
     1. Drop the sheet into its own folder: buffy/<name>/sprite.png and
        buffy/<name>/animation.json. Cells are 64 x 64 in one row. The json uses
        the handoff schema: `segments` [{ frame, ms }] and `durationMs`, plus any
        of `contacts` (named ms, clip-local and untrimmed), `anchors`,
        `airborne` [from, to], `playback` ("once" | "loop" | "poses"), `poses`
        ({ NAME: frame }), `adjacency` ([[a, b]]), and a `segmentsQuick` /
        `durationMsQuick` / `contactsQuick` variant (registered as <name>-quick).
     2. Add ONE line to SHEETS below:
          '<name>': sheet(require('./<name>/sprite.png'), require('./<name>/animation.json')),
   That is all. The CSS keyframes are generated from the json at runtime
   (injectClipCss), the track validator (hero/buffyTrack.test.js) picks up the
   new frame orders for the adjacency rule, and any entry in CLIP_FALLBACK with
   that name stops falling back.

   Frame identity: frame 0 of every sheet is the approved padded rest pose,
   pixel for pixel (verified by the validator against rest.png). REST_FRAMES
   lists any OTHER frame that is also the rest pose, so a clip that ends on it
   joins the next clip without a cut.

   Playback rules (BUFFY_HANDOFF sections 6 and 14): step-end keyframes, one
   cell at a time, integer display scale only, image-rendering: pixelated. */

const url = (m) => (m && m.default) || m;

function sheet(src, json) {
  return { src: url(src), json: url(json) };
}

/* eslint-disable global-require */
const SHEETS = {
  wave: sheet(require('./wave/sprite.png'), require('./wave/animation.json')),
  catch: sheet(require('./catch/sprite.png'), require('./catch/animation.json')),
  celebrate: sheet(require('./celebrate/sprite.png'), require('./celebrate/animation.json')),
  encourage: sheet(require('./encourage/sprite.png'), require('./encourage/animation.json')),
  think: sheet(require('./think/sprite.png'), require('./think/animation.json')),
  point: sheet(require('./point/sprite.png'), require('./point/animation.json')),
  jump: sheet(require('./jump/sprite.png'), require('./jump/animation.json')),
  connect: sheet(require('./connect/sprite.png'), require('./connect/animation.json')),
  'idle-breathe': sheet(require('./idle-breathe/sprite.png'), require('./idle-breathe/animation.json')),
  look: sheet(require('./look/sprite.png'), require('./look/animation.json')),
  listen: sheet(require('./listen/sprite.png'), require('./listen/animation.json')),
  // the section peeks (sections/BuffyPeek.jsx): hooves over a ledge at row
  // 34 (F1), a lean out from behind a card edge at column 23 (F2)
  'peek-over': sheet(require('./peek-over/sprite.png'), require('./peek-over/animation.json')),
  'peek-side': sheet(require('./peek-side/sprite.png'), require('./peek-side/animation.json')),
};
const REST_SRC = url(require('./rest.png'));
/* eslint-enable global-require */

/* Clips the story names that may be missing from SHEETS. A missing clip plays
   the approved rest pose for its planned duration (motion spec 5.2, last
   paragraph: REST is a legal stand-in for any clip), and its planned contacts
   still fire, so the UI keeps its timing. Every entry below is delivered today;
   the map stays as the safety net. catch-toss, prop-sheet, prop-plane,
   throw-plane and magnify were cancelled by the owner (2026-09-29: no props). */
export const CLIP_FALLBACK = {
  'idle-breathe': 'rest',
  look: 'rest',
  listen: 'rest',
  connect: 'rest',
  'connect-quick': 'rest',
};

/* Planned timing for the fallback case (motion spec 5.2). */
export const PLANNED = {
  'idle-breathe': { playback: 'loop', durationMs: 4800, contacts: {} },
  look: { playback: 'poses', durationMs: 0, contacts: {} },
  listen: { playback: 'loop', durationMs: 1800, contacts: { perk: 300, blink: 1200, rest: 1600 } },
  connect: { playback: 'once', durationMs: 1100, contacts: { grab: 200, carry: 400, extend: 500, plug: 600, release: 800 } },
  'connect-quick': { playback: 'once', durationMs: 700, contacts: { grab: 110, carry: 210, extend: 270, plug: 330, release: 480 } },
};

/* Contacts for approved sheets whose json does not carry them yet
   (clip-local, untrimmed ms, read off the frame starts). json wins. */
const DEFAULT_CONTACTS = {
  think: { chin: 800, blink: 1450, lower: 2200 },
  wave: { up: 600, wave: 900 },
  catch: { enter: 400, catch: 650, read: 800, blink: 1100, lower: 1500, tuck: 1800, rest: 2000 },
  celebrate: { apex: 800, land: 1250 },
};

/* Extra frames that are the rest pose (verified by the validator). */
const REST_FRAMES = { catch: [0, 10], connect: [0, 7], 'peek-over': [0, 10] };

/* Re-timings for the hero (design QA 2026-09-29, section c). The approved
   sheets are untouched; only the ORDER and HOLDS of their existing cells
   change, and every step between two cells is a pair the approved order
   already contains (proven by the validator). Goals: no single cell held over
   400 ms, no frozen middles, a crouch of 380 ms and 540 ms in the air.
     point   two taps instead of one 700 ms hold
     think   two blinks instead of two 650 ms holds
     jump    crouch 380 ms (frame 4 200 -> 80), air 540 ms (8: 80, 9: 110)
     listen  PERK broken by two blinks (cycle 1550 ms)
     idle    breathes every 400 ms, blinks once (cycle 1600 ms)
   Contacts are the cell starts of the new timing (untrimmed, clip-local). */
const seg = (list) => list.map(([frame, ms]) => ({ frame, ms }));
export const RETIME = {
  point: {
    segments: seg([[0, 500], [1, 100], [2, 100], [3, 200], [4, 350], [5, 120], [4, 250], [5, 120], [4, 250], [3, 100], [2, 100], [1, 100], [0, 300]]),
    contacts: { armOut: 700, hold: 900, tap: 1250, tap2: 1620, lower: 1990 },
  },
  think: {
    segments: seg([[0, 500], [1, 100], [2, 100], [3, 100], [4, 400], [5, 100], [4, 350], [5, 100], [4, 300], [6, 100], [2, 100], [1, 100], [0, 300]]),
    contacts: { chin: 800, blink: 1200, blink2: 1650, lower: 2050 },
  },
  jump: {
    segments: seg([[0, 500], [1, 100], [2, 100], [3, 100], [4, 80], [5, 50], [6, 50], [7, 50], [8, 80], [9, 110], [10, 100], [11, 50], [12, 50], [13, 50], [5, 100], [14, 100], [2, 100], [1, 100], [0, 600]]),
    airborne: [930, 1470],
    contacts: { takeoff: 930, apex: 1220, land: 1470, rest: 1870 },
  },
  listen: {
    segments: seg([[0, 150], [1, 400], [2, 100], [1, 350], [2, 100], [1, 350], [0, 100]]),
    contacts: { perk: 150, blink: 550, blink2: 1000 },
  },
  'idle-breathe': {
    segments: seg([[0, 200], [1, 400], [0, 200], [2, 50], [3, 100], [2, 50], [0, 200], [1, 400]]),
    contacts: {},
  },
};

/* Played window of each clip: from 100 ms before the first motion frame
   (motion spec 1.7) to the last motion frame. The rest tail is cut, so two
   gestures hand off through ONE 100 ms rest beat (the next clip's lead):
   the adjacency rule holds because every gesture's last cell sits next to the
   rest pose in its approved order. */
const TRIMS = {
  wave: { out: 300 },
  catch: { out: 300 },
  point: { out: 300 },
  think: { out: 300 },
  encourage: { out: 300 },
  celebrate: { out: 300 },
  jump: { in: 400, out: 600 },
  connect: { out: 200 },
  'connect-quick': { out: 160 },
};

const POSE_ORDER = ['REST', 'LEFT', 'RIGHT', 'UP', 'DOWN'];

function frameCountOf(json) {
  const n = json.frames || json.frameCount;
  if (Number.isFinite(n)) return n;
  let max = 0;
  (json.segments || []).forEach((s) => { max = Math.max(max, s.frame); });
  (json.order || []).forEach((f) => { max = Math.max(max, f); });
  Object.values(json.poses || {}).forEach((f) => { max = Math.max(max, f); });
  return max + 1;
}

function playbackOf(json) {
  if (json.playback === 'loop' || json.playback === 'poses') return json.playback;
  if (json.poses && !json.segments) return 'poses';
  return 'once';
}

function defaultTrimIn(segments) {
  const first = segments[0];
  return first && first.frame === 0 && first.ms > 100 ? first.ms - 100 : 0;
}

function makeClip(name, sheetName, src, json, variant) {
  const playback = playbackOf(json);
  const approved = variant ? json[`segments${variant}`] : json.segments || [];
  const re = RETIME[name];
  const segments = re ? re.segments : approved;
  const durationMs = re || variant
    ? segments.reduce((sum, s) => sum + s.ms, 0)
    : json.durationMs || segments.reduce((sum, s) => sum + s.ms, 0);
  const contacts = {
    ...(DEFAULT_CONTACTS[name] || {}),
    ...((variant ? json[`contacts${variant}`] : json.contacts) || {}),
    ...(re ? re.contacts : {}),
  };
  const trims = TRIMS[name] || {};
  const trimIn = playback === 'once' ? (trims.in ?? defaultTrimIn(segments)) : 0;
  const trimOut = playback === 'once' ? (trims.out ?? 0) : 0;
  const poses = json.poses || (playback === 'poses'
    ? Object.fromEntries(POSE_ORDER.map((p, i) => [p, i]))
    : null);
  const orders = [json.order, variant ? json[`order${variant}`] : json.orderQuick].filter(Array.isArray);
  return {
    name,
    sheet: sheetName,
    src,
    delivered: true,
    playback,
    frames: frameCountOf(json),
    segments,
    durationMs,
    contacts,
    anchors: json.anchors || {},
    airborne: (re && re.airborne) || (Array.isArray(json.airborne) ? json.airborne : null),
    poses,
    adjacency: json.adjacency || [],
    exitFrames: json.exitFrames || null,
    orders,
    // the sheet's own approved cell sequence (the played one may be re-timed)
    approved: approved.map((x) => x.frame),
    retimed: !!re,
    restFrames: REST_FRAMES[sheetName] || [0],
    trimIn,
    trimOut,
    effDur: playback === 'once' ? durationMs - trimIn - trimOut : 0,
  };
}

function buildRegistry(sheets) {
  const clips = {
    rest: {
      name: 'rest', sheet: 'rest', src: REST_SRC, delivered: true, playback: 'hold', frames: 1,
      segments: [{ frame: 0, ms: 1 }], durationMs: 0, contacts: {}, anchors: {}, airborne: null,
      poses: null, adjacency: [], exitFrames: null, orders: [], approved: [0], retimed: false, restFrames: [0], trimIn: 0, trimOut: 0, effDur: 0,
    },
  };
  Object.entries(sheets).forEach(([name, { src, json }]) => {
    clips[name] = makeClip(name, name, src, json, '');
    if (Array.isArray(json.segmentsQuick)) clips[`${name}-quick`] = makeClip(`${name}-quick`, name, src, json, 'Quick');
  });
  return clips;
}

export const CLIPS = buildRegistry(SHEETS);
export const SHEET_JSON = Object.fromEntries(Object.entries(SHEETS).map(([k, v]) => [k, v.json]));
export const SHEET_SRC = Object.fromEntries(Object.entries(SHEETS).map(([k, v]) => [k, v.src]));
export { REST_SRC };

/* The clip a track entry actually plays. A missing clip resolves to its
   CLIP_FALLBACK (REST) and keeps its PLANNED duration and contacts. */
export function resolveClip(name, registry = CLIPS) {
  if (registry[name]) return registry[name];
  const fb = registry[CLIP_FALLBACK[name]] || registry.rest;
  const plan = PLANNED[name] || { playback: 'once', durationMs: 0, contacts: {} };
  return {
    ...fb,
    name,
    delivered: false,
    playback: 'hold',
    durationMs: plan.durationMs,
    contacts: plan.contacts,
    effDur: plan.playback === 'once' ? plan.durationMs : 0,
    trimIn: 0,
    trimOut: 0,
    standIn: fb.name,
  };
}

/* The sheet frame shown at clip-local (untrimmed) ms `local`. */
export function frameAt(clip, local, pose) {
  if (clip.playback === 'hold') return 0;
  if (clip.playback === 'poses') return (clip.poses && clip.poses[pose]) || 0;
  const segs = clip.segments;
  if (!segs.length) return 0;
  let ms = clip.playback === 'loop' && clip.durationMs > 0
    ? ((local % clip.durationMs) + clip.durationMs) % clip.durationMs
    : Math.max(0, local);
  for (let i = 0; i < segs.length; i += 1) {
    if (ms < segs[i].ms) return segs[i].frame;
    ms -= segs[i].ms;
  }
  return segs[segs.length - 1].frame;
}

/* A frame's identity across sheets: every rest-pose cell is 'REST'. */
export function frameKey(clip, frame) {
  if (clip.sheet === 'rest' || clip.restFrames.includes(frame)) return 'REST';
  return `${clip.sheet}:${frame}`;
}

/* The adjacency table (motion spec 3.2): two frames are adjacent when they
   appear one after the other in some approved order (segments, order arrays,
   quick orders), or in a sheet's explicit `adjacency` list. Undirected. */
export function buildAdjacency(registry = CLIPS) {
  const adj = new Map();
  const link = (a, b) => {
    if (a === b) return;
    if (!adj.has(a)) adj.set(a, new Set());
    if (!adj.has(b)) adj.set(b, new Set());
    adj.get(a).add(b);
    adj.get(b).add(a);
  };
  Object.values(registry).forEach((clip) => {
    // APPROVED sequences only (json segments and orders), never our re-timings
    const seqs = [clip.approved || [], ...clip.orders];
    seqs.forEach((seq) => {
      for (let i = 1; i < seq.length; i += 1) link(frameKey(clip, seq[i - 1]), frameKey(clip, seq[i]));
      if (clip.playback === 'loop' && seq.length > 1) link(frameKey(clip, seq[seq.length - 1]), frameKey(clip, seq[0]));
    });
    clip.adjacency.forEach(([a, b]) => link(frameKey(clip, a), frameKey(clip, b)));
  });
  return adj;
}

export const isAdjacent = (adj, a, b) => a === b || (adj.has(a) && adj.get(a).has(b));

/* ---------- runtime CSS (generated once from animation.json) ----------
   For the reusable player (BuffySprite.jsx): the sprite is a clipping cell
   holding one strip of the atlas (buffy/atlas.js). A frame change is a
   step-end translateX of the strip by whole cells: transform only
   (Chromebook guardrail), integer px, pixelated. Each clip gets two identical
   keyframe names (a / b) so flipping data-phase restarts it cleanly.
   (The hero's BuffyActor sets the same transforms from its master clock.) */

const pct = (ms, total) => `${((ms / total) * 100).toFixed(4)}%`;
export const cellShift = (frame) => (frame === 0 ? 'translate3d(0,0,0)' : `translate3d(calc(var(--bx-cell) * -${frame}),0,0)`);

function keyframesFor(clip, suffix) {
  const total = clip.durationMs;
  let at = 0;
  const stops = [];
  clip.segments.forEach((s) => {
    stops.push(`${pct(at, total)}{transform:${cellShift(s.frame)}}`);
    at += s.ms;
  });
  stops.push(`100%{transform:${cellShift(clip.segments[clip.segments.length - 1].frame)}}`);
  return `@keyframes bx-${clip.name}-${suffix}{${stops.join('')}}`;
}

export function clipCss(registry = CLIPS) {
  const rules = [
    '.bx-play{transform:translate3d(0,0,0);animation:none}',
    '.bx-play[data-paused="true"]{animation-play-state:paused!important}',
  ];
  Object.values(registry).forEach((clip) => {
    if (clip.name === 'rest') return;
    const sel = `.bx-play[data-clip="${clip.name}"]`;
    if (clip.playback === 'poses') {
      Object.entries(clip.poses || {}).forEach(([pose, frame]) => {
        rules.push(`${sel}[data-pose="${pose}"]{transform:${cellShift(frame)}}`);
      });
      return;
    }
    rules.push(`${sel}{animation:bx-${clip.name}-a ${clip.durationMs}ms step-end 1 both}`);
    rules.push(`${sel}[data-loop="true"]{animation-iteration-count:infinite}`);
    rules.push(`${sel}[data-phase="b"]{animation-name:bx-${clip.name}-b}`);
    rules.push(keyframesFor(clip, 'a'), keyframesFor(clip, 'b'));
  });
  return rules.join('\n');
}

let injected = false;
export function injectClipCss() {
  if (injected || typeof document === 'undefined') return;
  injected = true;
  if (document.querySelector('style[data-bx-clips]')) return; // hot reload
  const style = document.createElement('style');
  style.setAttribute('data-bx-clips', '');
  style.textContent = clipCss();
  document.head.appendChild(style);
}
